// CommunityEvents Component - Cross-group events view

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  UsersRound,
  Clock,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  getUserUpcomingEvents,
  getPublicUpcomingEvents,
  type GroupEventWithGroup,
} from '../../services/groupsService';
import { Timestamp } from '@firebase/firestore';
import { format, isToday, isTomorrow, isThisWeek } from 'date-fns';
import { de } from 'date-fns/locale';

interface CommunityEventsProps {
  onBack: () => void;
  onViewGroup: (groupId: string) => void;
  onViewPlace?: (placeId: string) => void;
}

type ViewMode = 'my' | 'all';

export function CommunityEvents({
  onBack,
  onViewGroup,
  onViewPlace,
}: CommunityEventsProps) {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>(user ? 'my' : 'all');
  const [events, setEvents] = useState<GroupEventWithGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        let fetchedEvents: GroupEventWithGroup[];
        if (viewMode === 'my' && user) {
          fetchedEvents = await getUserUpcomingEvents(user.uid, 50);
        } else {
          fetchedEvents = await getPublicUpcomingEvents(50);
        }
        setEvents(fetchedEvents);
      } catch (err) {
        console.error('Error loading events:', err);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [viewMode, user]);

  const formatEventDate = (date: Timestamp | Date) => {
    const d = date instanceof Timestamp ? date.toDate() : date;
    if (isToday(d)) return 'Heute';
    if (isTomorrow(d)) return 'Morgen';
    return format(d, 'EEE, d. MMM', { locale: de });
  };

  const formatEventTime = (date: Timestamp | Date) => {
    const d = date instanceof Timestamp ? date.toDate() : date;
    return format(d, 'HH:mm', { locale: de });
  };

  // Group events by date category
  const groupEventsByDate = (events: GroupEventWithGroup[]) => {
    const today: GroupEventWithGroup[] = [];
    const tomorrow: GroupEventWithGroup[] = [];
    const thisWeek: GroupEventWithGroup[] = [];
    const later: GroupEventWithGroup[] = [];

    events.forEach(event => {
      const d = event.startDate instanceof Timestamp ? event.startDate.toDate() : event.startDate;
      if (isToday(d)) today.push(event);
      else if (isTomorrow(d)) tomorrow.push(event);
      else if (isThisWeek(d)) thisWeek.push(event);
      else later.push(event);
    });

    return { today, tomorrow, thisWeek, later };
  };

  const grouped = groupEventsByDate(events);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Events</h1>
              <p className="text-sm text-gray-500">
                {viewMode === 'my' ? 'Aus deinen Gruppen' : 'Alle oeffentlichen Events'}
              </p>
            </div>
          </div>

          {/* Toggle */}
          {user && (
            <div className="flex gap-1 mt-4 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('my')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'my'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Users className="w-4 h-4 inline mr-2" />
                Meine Events
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  viewMode === 'all'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Calendar className="w-4 h-4 inline mr-2" />
                Alle Events
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Keine Events gefunden
            </h2>
            <p className="text-gray-500 mb-4">
              {viewMode === 'my'
                ? 'Deine Gruppen haben aktuell keine geplanten Events.'
                : 'Es gibt aktuell keine oeffentlichen Events.'}
            </p>
            {viewMode === 'my' && (
              <button
                onClick={() => setViewMode('all')}
                className="text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Oeffentliche Events anzeigen
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Today */}
            {grouped.today.length > 0 && (
              <EventSection
                title="Heute"
                events={grouped.today}
                onViewGroup={onViewGroup}
                onViewPlace={onViewPlace}
                formatDate={formatEventDate}
                formatTime={formatEventTime}
              />
            )}

            {/* Tomorrow */}
            {grouped.tomorrow.length > 0 && (
              <EventSection
                title="Morgen"
                events={grouped.tomorrow}
                onViewGroup={onViewGroup}
                onViewPlace={onViewPlace}
                formatDate={formatEventDate}
                formatTime={formatEventTime}
              />
            )}

            {/* This Week */}
            {grouped.thisWeek.length > 0 && (
              <EventSection
                title="Diese Woche"
                events={grouped.thisWeek}
                onViewGroup={onViewGroup}
                onViewPlace={onViewPlace}
                formatDate={formatEventDate}
                formatTime={formatEventTime}
              />
            )}

            {/* Later */}
            {grouped.later.length > 0 && (
              <EventSection
                title="Spaeter"
                events={grouped.later}
                onViewGroup={onViewGroup}
                onViewPlace={onViewPlace}
                formatDate={formatEventDate}
                formatTime={formatEventTime}
              />
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// Event Section Component
interface EventSectionProps {
  title: string;
  events: GroupEventWithGroup[];
  onViewGroup: (groupId: string) => void;
  onViewPlace?: (placeId: string) => void;
  formatDate: (date: Timestamp | Date) => string;
  formatTime: (date: Timestamp | Date) => string;
}

function EventSection({
  title,
  events,
  onViewGroup,
  onViewPlace,
  formatDate,
  formatTime,
}: EventSectionProps) {
  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
      <div className="space-y-3">
        {events.map((event) => (
          <EventCard
            key={`${event.groupId}-${event.id}`}
            event={event}
            onViewGroup={onViewGroup}
            onViewPlace={onViewPlace}
            formatDate={formatDate}
            formatTime={formatTime}
          />
        ))}
      </div>
    </section>
  );
}

// Event Card Component
interface EventCardProps {
  event: GroupEventWithGroup;
  onViewGroup: (groupId: string) => void;
  onViewPlace?: (placeId: string) => void;
  formatDate: (date: Timestamp | Date) => string;
  formatTime: (date: Timestamp | Date) => string;
}

function EventCard({
  event,
  onViewGroup,
  onViewPlace,
  formatDate,
  formatTime,
}: EventCardProps) {
  // Get location display
  const getLocationDisplay = () => {
    if (event.places && event.places.length > 0) {
      return event.places.map(p => p.placeName).join(', ');
    }
    if (event.placeName) return event.placeName;
    if (event.customLocation) return event.customLocation;
    return null;
  };

  const location = getLocationDisplay();
  const firstPlaceId = event.places?.[0]?.placeId || event.placeId;

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
      onClick={() => onViewGroup(event.groupId)}
    >
      {/* Cover Image */}
      {event.coverImageUrl && (
        <div className="h-32 bg-gray-100">
          <img
            src={event.coverImageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-4">
        {/* Date and Time */}
        <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium mb-2">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(event.startDate)}</span>
          {!event.isAllDay && (
            <>
              <Clock className="w-4 h-4 ml-2" />
              <span>{formatTime(event.startDate)} Uhr</span>
            </>
          )}
        </div>

        {/* Title */}
        <h3 className="font-semibold text-gray-900 text-lg mb-1">{event.title}</h3>

        {/* Description */}
        {event.description && (
          <p className="text-gray-600 text-sm line-clamp-2 mb-3">{event.description}</p>
        )}

        {/* Location */}
        {location && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (firstPlaceId && onViewPlace) {
                onViewPlace(firstPlaceId);
              }
            }}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-indigo-600 transition-colors mb-3"
          >
            <MapPin className="w-4 h-4" />
            <span className="truncate">{location}</span>
          </button>
        )}

        {/* Footer: Group and Attendees */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            {event.groupAvatarUrl ? (
              <img
                src={event.groupAvatarUrl}
                alt=""
                className="w-6 h-6 rounded-full object-cover"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                <UsersRound className="w-3 h-3 text-indigo-500" />
              </div>
            )}
            <span className="text-sm text-gray-600">{event.groupName}</span>
          </div>

          <div className="flex items-center gap-1 text-sm text-gray-500">
            <Users className="w-4 h-4" />
            <span>{event.attendeeCount} Teilnehmer</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommunityEvents;
